// Error monitoring + reporting.
//
// Sentry is OPTIONAL and lazy-loaded: it only initializes (and only enters the
// bundle as a separate chunk) when VITE_SENTRY_DSN is set for this build. That
// keeps schools that don't use Sentry from paying any bundle/runtime cost.
//
// Regardless of Sentry, we always install a global Vue error handler and a
// window-level safety net so unexpected errors surface consistently instead of
// dying silently.
//
// FERPA note: sendDefaultPii is false and we attach only uid/email/role via
// setMonitoringUser. Review your Sentry project's data-scrubbing settings before
// enabling in production, since stack frames/breadcrumbs can incidentally carry
// student data.

let sentry = null

export async function initMonitoring(app, router) {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (dsn) {
    try {
      sentry = await import('@sentry/vue')
      sentry.init({
        app,
        dsn,
        // Use the per-school Firebase project id as the environment so issues
        // are attributable to a specific school.
        environment: import.meta.env.VITE_FIREBASE_PROJECT_ID || import.meta.env.MODE,
        integrations: router ? [sentry.browserTracingIntegration({ router })] : [],
        tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0.1),
        sendDefaultPii: false
      })
    } catch (e) {
      console.error('Sentry init failed:', e)
      sentry = null
    }
  }

  // Chain a Vue error handler. When Sentry is active it has already installed
  // its own handler (which captured the error); we read and call it so capture
  // is preserved while still logging to the console.
  const prevHandler = app.config.errorHandler
  app.config.errorHandler = (err, instance, info) => {
    console.error('[vue error]', err, info)
    if (typeof prevHandler === 'function') prevHandler(err, instance, info)
  }

  // Safety net for errors outside Vue's render tree. When Sentry is active its
  // global handlers already capture these, so we only add ours when it isn't.
  if (!sentry && typeof window !== 'undefined') {
    window.addEventListener('error', (e) => console.error('[window error]', e.error || e.message))
    window.addEventListener('unhandledrejection', (e) =>
      console.error('[unhandledrejection]', e.reason)
    )
  }
}

/**
 * Report a handled error (e.g. from a try/catch). Always logs to the console;
 * also sends to Sentry when configured. Use this in catch blocks where the
 * error is swallowed but you still want visibility.
 */
export function reportError(error, context = {}) {
  console.error('[error]', error, context)
  if (sentry) {
    const err = error instanceof Error ? error : new Error(String(error))
    sentry.captureException(err, { extra: context })
  }
}

/**
 * Attach (or clear) the current user on the monitoring context so reports are
 * attributable. No-op when Sentry isn't configured.
 */
export function setMonitoringUser(user) {
  if (!sentry) return
  sentry.setUser(user ? { id: user.uid, email: user.email, role: user.role } : null)
}

export function isMonitoringActive() {
  return !!sentry
}
